import Vue from 'vue'

export default {
  SET_LOADING (state, { key, value }) {
    if (!state.loading) Vue.set(state, 'loading', {})
    Vue.set(state.loading, key, value)
  },

  SET_ROOMS (state, rooms) {
    const current = Array.isArray(state.rooms) ? state.rooms : []
    const byId = new Map(current.map(r => [r.id, r]))

    const next = (rooms || []).map(r => {
      const prev = byId.get(r.id) || {}
      return Object.assign({ unreadCount: 0 }, r, {
        unreadCount: prev.unreadCount || 0
      })
    })

    Vue.set(state, 'rooms', next)
  },

  UPSERT_ROOM (state, room) {
    if (!Array.isArray(state.rooms)) Vue.set(state, 'rooms', [])

    const idx = state.rooms.findIndex(r => r.id === room.id)
    if (idx >= 0) {
      // preserva unreadCount atual
      const unreadCount = state.rooms[idx].unreadCount || 0
      state.rooms.splice(idx, 1, Object.assign({}, state.rooms[idx], room, { unreadCount }))
    } else {
      state.rooms.unshift(Object.assign({ unreadCount: 0 }, room))
    }
  },

  SET_MESSAGES (state, { roomId, messages }) {
    if (!state.messagesByRoom) Vue.set(state, 'messagesByRoom', {})
    Vue.set(state.messagesByRoom, roomId, Array.isArray(messages) ? messages : [])
  },

  /**
   * Aceita:
   *  - ADD_MESSAGE(state, message)
   *  - ADD_MESSAGE(state, { roomId, message })
   */
  ADD_MESSAGE (state, { roomId, message, silent }) {
    const rid = String(roomId)

    if (!state.messagesByRoom) Vue.set(state, 'messagesByRoom', {})
    if (!state.messagesByRoom[rid]) Vue.set(state.messagesByRoom, rid, [])

    // se a mensagem já existe, faz merge (atualiza conteúdo, arquivos, etc sem perder read/unread)
    if (message && message.id != null) {
      const mid = String(message.id)
      const list = state.messagesByRoom[rid]
      const existingIdx = list.findIndex(m => m && m.id != null && String(m.id) === mid)

      if (existingIdx >= 0) {
        const prev = list[existingIdx] || {}
        const prevFiles = Array.isArray(prev.files) ? prev.files : []
        const nextFiles = Array.isArray(message.files) ? message.files : null

        const merged = Object.assign({}, prev, message)

        // se o payload novo não veio com files, preserva os que já estavam
        if (nextFiles === null) merged.files = prevFiles
        else merged.files = nextFiles

        list.splice(existingIdx, 1, merged)

        // atualiza preview do room (sem unread/reorder quando silencioso)
        const ridx = state.rooms.findIndex(r => String(r.id) === rid)
        if (ridx >= 0) {
          const room = state.rooms[ridx]
          const updatedRoom = Object.assign({}, room, { lastMessage: merged })
          state.rooms.splice(ridx, 1, updatedRoom)
        }
        return
      }
    }

    state.messagesByRoom[rid].push(message)

    // atualiza preview + unread + reordena
    const idx = state.rooms.findIndex(r => String(r.id) === rid)
    if (idx >= 0) {
      const room = state.rooms[idx]

      // sempre atualiza o preview
      const updatedBase = Object.assign({}, room, {
        lastMessage: message,
        lastMessageTime: message && message.timestamp ? message.timestamp : (room.lastMessageTime || null)
      })

      // se for silent, só atualiza o preview (ex: mensagem própria ou atualização de mensagem existente), sem incrementar unread ou reordenar
      if (silent) {
        state.rooms.splice(idx, 1, updatedBase)
        return
      }

      const isActive = state.activeRoomId != null && String(state.activeRoomId) === rid

      const updated = Object.assign({}, updatedBase, {
        unreadCount: isActive ? 0 : ((room.unreadCount || 0) + 1)
      })

      // remove e re-insere no início para reordenar
      state.rooms.splice(idx, 1)
      state.rooms.unshift(updated)
    }
  },

  SET_ACTIVE_ROOM (state, roomId) {
    // guarda sempre como string (evita mismatch)
    Vue.set(state, 'activeRoomId', roomId != null ? String(roomId) : null)

    if (Array.isArray(state.rooms)) {
      const idx = state.rooms.findIndex(r => String(r.id) === String(roomId))
      if (idx >= 0) {
        const r = state.rooms[idx]
        // só zera unread
        state.rooms.splice(idx, 1, Object.assign({}, r, { unreadCount: 0 }))
      }
    }
  },

  ADD_ROOM (state, room) {
    if (!Array.isArray(state.rooms)) Vue.set(state, 'rooms', [])

    const exists = state.rooms.some(r => r.id === room.id)
    if (!exists) {
      state.rooms.unshift(Object.assign({ unreadCount: 0 }, room))
    }
  },

  TOGGLE_CHAT (state) {
    if (!state.ui) Vue.set(state, 'ui', { isOpen: false })
    state.ui.isOpen = !state.ui.isOpen
  },

  OPEN_CHAT (state) {
    if (!state.ui) Vue.set(state, 'ui', { isOpen: false })
    state.ui.isOpen = true
  },

  CLOSE_CHAT (state) {
    if (!state.ui) Vue.set(state, 'ui', { isOpen: false })
    state.ui.isOpen = false
  },

  SET_SEARCH_RESULTS (state, users) {
    Vue.set(state, 'searchResults', Array.isArray(users) ? users : [])
  },

  MARK_MESSAGES_READ (state, { roomId, messageIds }) {
    if (!state.messagesByRoom || !state.messagesByRoom[roomId]) return

    const set = new Set(messageIds || [])
    const updated = state.messagesByRoom[roomId].map(m => {
      if (m && set.has(m.id)) return Object.assign({}, m, { read: true })
      return m
    })
    Vue.set(state.messagesByRoom, roomId, updated)
  },

  UPDATE_LAST_MESSAGE (state, { roomId, message }) {
    const rid = String(roomId)
    const rooms = state.rooms || []
    const room = rooms.find(r => String(r.id) === rid)
    if (room) {
      room.lastMessage = message
      room.lastMessageTime = message && message.timestamp ? message.timestamp : null
    }
  },

  SET_CONNECTED (state, value) {
    Vue.set(state, 'connected', !!value)
  },

  SET_USER_TYPING (state, { roomId, userId, typing }) {
    if (!state.typingByRoom) Vue.set(state, 'typingByRoom', {})
    if (!state.typingByRoom[roomId]) Vue.set(state.typingByRoom, roomId, {})
    Vue.set(state.typingByRoom[roomId], userId, !!typing)
  }
}



// WEBPACK FOOTER //
// ./src/plugins/chat-plugin/store/mutations.js